import React from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import FolderIcon from '@SonarCX/components/icons/FolderIcon'
import FileIcon from '@SonarCX/components/icons/FileIcon'
import TagIcon from '@SonarCX/components/icons/TagIcon'
import ArticleAvatar from '@SonarCX/components/atoms/ArticleAvatar'
import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import ArticleOptionsMenu from '@SonarCX/components/molecules/ArticleOptionsMenu'
import Card from '@SonarCX/components/atoms/Card'

import { formatDateRelative } from '@SonarCX/utilities'
import { selectArticle } from '@SonarCX/slices/article'
import useFavorites from '@SonarCX/hooks/useFavorites'
import useStyles from './styles'

type ArticleCardProps = {
  article: APIArticleGeneral
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const dispatch = useAppDispatch()

  const { favoriteArticles } = useFavorites()

  const isFavorite = React.useMemo(
    () => favoriteArticles.some(x => x.item_id === article.id),
    [favoriteArticles, article.id]
  )
  const isFolder = React.useMemo(
    () => article.type === 'folder',
    [article.type]
  )
  const titleText = React.useMemo(() => {
    const { title, total_items = '0' } = article

    if (!isFolder) {
      return title
    }

    return `${title} (${total_items})`
  }, [article, isFolder])
  const numTags = React.useMemo(() => article.tags.length, [article.tags])

  const handleClick = React.useCallback(() => {
    dispatch(selectArticle(article.id))
  }, [dispatch, article.id])

  const classes = useStyles()

  const [statusClass, statusColor, status] = React.useMemo(() => {
    const { published_at, archived_at } = article

    if (published_at) {
      return [classes.statusPublished, '#5CC043', 'Published']
    }

    if (archived_at) {
      return [classes.statusUnpublished, '#8F8F8F', 'Unpublished']
    }

    if (published_at === null) {
      return [classes.statusDraft, '#FFB800', 'Draft']
    }

    return ['', '', '']
  }, [
    article,
    classes.statusPublished,
    classes.statusUnpublished,
    classes.statusDraft
  ])

  return (
    <Card onClick={handleClick} className={classes.root}>
      <div className={classes.titleRow}>
        {isFolder ? <FolderIcon /> : <FileIcon />}

        <div>{titleText}</div>
      </div>

      {!isFolder && numTags > 0 && (
        <div className={classes.tags}>
          {article.tags.slice(0, 2).map((tag, idx) => (
            <span key={`${tag}-${idx}`} className={classes.tag}>
              <TagIcon /> {tag}
            </span>
          ))}

          {numTags > 2 && <span>+{numTags - 2} more</span>}
        </div>
      )}

      <div className={classes.user}>
        <div>Created by</div>

        <ArticleAvatar useDiv onClick={() => {}} userId={article.created_by} />
      </div>

      <div className={classes.user}>
        <div>Last edited by</div>

        <ArticleAvatar
          useDiv
          onClick={() => {}}
          userId={article.last_updated_by}
        />

        {article.updated_at && (
          <div>- {formatDateRelative(article.updated_at, true)}</div>
        )}
      </div>

      <div className={classes.statusRow}>
        {statusClass ? (
          <div className={clsx(classes.statusBlock, statusClass)}>
            <svg
              width='8'
              height='8'
              viewBox='0 0 8 8'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='4' cy='4' r='4' fill={statusColor} />
            </svg>

            {status}
          </div>
        ) : (
          <div />
        )}

        <div className={classes.controls}>
          {!isFolder && (
            <FavoriteButton
              itemId={article.id}
              type='article'
              isFavorite={isFavorite}
            />
          )}

          <ArticleOptionsMenu articleId={article.id} className={classes.menu} />
        </div>
      </div>
    </Card>
  )
}

export default ArticleCard
